import React from "react";
import { Edit2, Loader2 } from "lucide-react";

type EditStatusModalProps = Readonly<{
  isOpen: boolean;
  itemName: string;
  currentStatus: string;
  newStatus: string;
  entityLabel?: string;
  primaryColor?: "blue" | "emerald";
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  isConfirming?: boolean;
}>;

export function EditStatusModal({
  isOpen,
  itemName,
  currentStatus,
  newStatus,
  entityLabel = "Update status",
  primaryColor = "blue",
  onConfirm,
  onCancel,
  isConfirming = false,
}: EditStatusModalProps) {
  if (!isOpen) return null;

  const colorClasses = {
    blue: {
      icon: "bg-blue-100",
      iconSvg: "text-blue-600",
      button: "bg-blue-600 hover:bg-blue-700",
      newStatusActive: "text-red-600",
      newStatusInactive: "text-green-600",
    },
    emerald: {
      icon: "bg-emerald-100",
      iconSvg: "text-emerald-600",
      button: "bg-emerald-600 hover:bg-emerald-700",
      newStatusActive: "text-red-600",
      newStatusInactive: "text-green-600",
    },
  };

  const colors = colorClasses[primaryColor];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        <div className="p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div
              className={`w-12 h-12 ${colors.icon} rounded-full flex items-center justify-center`}
            >
              <Edit2 className={`w-6 h-6 ${colors.iconSvg}`} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Change Status
              </h3>
              <p className="text-sm text-gray-500">{entityLabel}</p>
            </div>
          </div>
          <p className="text-gray-700 mb-6">
            Change <span className="font-semibold text-gray-900">{itemName}</span>
            {' '}status to{' '}
            <span
              className={`font-semibold ${
                newStatus === "Inactive" ? colors.newStatusActive : colors.newStatusInactive
              }`}
            >
              {newStatus}?
            </span>
          </p>
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="flex-1 px-4 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isConfirming}
              className={`flex-1 px-4 py-2.5 ${colors.button} text-white rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2 font-medium`}
            >
              {isConfirming && <Loader2 className="w-4 h-4 animate-spin" />}
              Change
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
